import { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ButtonContainer,
  IconLibrary,
  Layout,
  PageSection,
  StackablePageButtons,
  PermissionButton,
} from '@product-framework/index';
import { editSchema } from '../../schemas';
import validationSchema from './validations';
import WorkflowDisplay from '../../components/WorkflowDisplay';

const FORM_ID = 'MAINTENANCE_WORKFLOW_EDIT_FORM';
const PERMISSION_SP = 'WORKFLOW';

function WorkflowEdit() {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/workflows/${pageId}`;

  const [loading, setLoading] = useState(true);
  const [basicInfoError, setBasicInfoError] = useState(false);

  const preProcessRequestData = data => {
    const {
      isActive,
      createdByUserId,
      lastModifiedByUserId,
      id,
      form,
      ...other
    } = data;
    return {
      isActive: isActive === 'true',
      ...other,
      formId: data.form?.value,
    };
  };

  const preProcessFetchData = data => {
    const { form, ...other } = data;
    return {
      ...other,
      form: {
        label: form?.formName,
        value: form?.id,
      },
    };
  };

  const fetchDefaultValues = useCallback(async () => {
    try {
      setBasicInfoError(false);
      setLoading(true);
      const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/workflows/${pageId}`;
      const response = await get(getPageDataApiUrl);
      if (response?.ok) {
        setLoading(false);
        return {
          data: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      setLoading(false);
      setBasicInfoError(true);
      return {
        data: null,
        errors: response?.errors || null,
      };
    } catch (err) {
      setLoading(false);
      setBasicInfoError(true);
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [get, getApiUrl, pageId]);

  const onSubmit = async data => {
    try {
      setLoading(true);
      const requestData = preProcessRequestData(data);
      const response = await put(updateApiUrl, { ...requestData });
      if (response?.ok) {
        snackbar.showMessage(
          formatMessage({
            id: 'WORKFLOW_WAS_SAVED_SUCCESSFULLY',
            defaultMessage: 'Workflow was saved successfully',
          }),
        );
        setLoading(false);
        return {
          nextValues: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      setLoading(false);
      return {
        nextValues: data,
        errors: response?.errors,
      };
    } catch (err) {
      setLoading(false);
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'WORKFLOWS',
        defaultMessage: 'Workflows',
      }),
      icon: IconLibrary.Workflows,
      onClick: () => {
        navigate('/workflows');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_WORKFLOW',
        defaultMessage: 'Edit Workflow',
      }),
      icon: IconLibrary.Workflow,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_WORKFLOW',
        defaultMessage: 'Create Workflow',
      }),
      Icon: IconLibrary.Workflow,
      variant: 'outlined',
      onClick: () => navigate('/workflows/create'),
      permissionName: 'CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES',
    },
    {
      label: formatMessage({
        id: 'LIST_WORKFLOWS',
        defaultMessage: 'List Workflows',
      }),
      Icon: IconLibrary.Workflows,
      variant: 'outlined',
      onClick: () => navigate('/workflows'),
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'EDIT_WORKFLOW',
          defaultMessage: 'Edit Workflow',
        })}>
        <Box>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permissionEntities={[PERMISSION_SP]}
            onSubmit={onSubmit}
            schema={editSchema}
            validationSchema={validationSchema(formatMessage, getApiUrl, get)}
            fetchDefaultValues={fetchDefaultValues}>
            <ButtonContainer>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'SAVE_WORKFLOW',
                  defaultMessage: 'Save Workflow',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => formRef.current?.reset({})}>
                {formatMessage({
                  id: 'RESET',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </ButtonContainer>
          </Form>
        </Box>
      </PageSection>

      {!loading && !basicInfoError && (
        <PageSection
          label={formatMessage({
            id: 'WORKFLOW_VISUALIZATION',
            defaultMessage: 'Workflow Visualization',
          })}>
          <Box>
            <WorkflowDisplay workflowId={pageId} />
          </Box>
        </PageSection>
      )}
    </Layout>
  );
}

export default observer(WorkflowEdit);
